import type { Flag } from '@/services/flags'
import type { ParsedQuestionnaire } from '@/services/med-req-detail'
import * as Sentry from '@sentry/react'

const BATCH_FLAGS = [
  '7c356d8d4f0a5ff90a4089397582bd', // staging/dev
  'bd00417aa21f4e5c4c2b323b44706f' // prod
]

export function parseQuestionnaire(str: string, flagsMap: Record<string, Flag>): ParsedQuestionnaire[] {
  const cleanedJsonString = str.replace(/[\n\r\t]/g, '')
  let result = null

  try {
    result = JSON.parse(cleanedJsonString) as Array<unknown>
  } catch (error) {
    result = [
      {
        title: 'Error parsing questionnaire',
        description: 'The questionnaire JSON could not be parsed. Please check the format!!!'
      }
    ]
    console.error('Error parsing questionnaire JSON:', error)
    Sentry.captureMessage('Error parsing questionnaire JSON', {
      level: 'error',
      contexts: {
        questionnaire: {
          raw: str
        }
      }
    })
  }

  const parseLinksAndHasFlags = (obj: unknown): unknown => {
    if (Array.isArray(obj)) {
      return obj.map(parseLinksAndHasFlags)
    }

    if (typeof obj !== 'object' || obj === null) {
      return obj
    }

    const record = obj as Record<string, unknown>
    const flags = record.flags as string[] | undefined

    if (flags && flags.length > 0) {
      const validFlags = flags.filter((flag: string) => flagsMap[flag] && !BATCH_FLAGS.includes(flag))
      const options = record.options as Array<Record<string, unknown>> | undefined
      const hasSomeNestedBatchFlag = BATCH_FLAGS.some(entry =>
        options?.some(opt => {
          const logicFlags = (opt?.logic as Record<string, unknown> | undefined)?.flags as string[] | undefined
          return logicFlags?.some((flag: string) => flag.indexOf(entry) > -1)
        })
      )

      record.hasFlags = !!validFlags.length && !hasSomeNestedBatchFlag
      record.isBatch =
        flags.some(flag => BATCH_FLAGS.includes(flag)) ||
        hasSomeNestedBatchFlag ||
        ((record.tags as string[]) ?? []).includes('batch')
    }

    if ((record.title as string | undefined)?.includes('Provide picture') || record.type === 'uploader') {
      if (Array.isArray(record.answer)) {
        record.links = record.answer
        record.link = (record.answer[0] as string) || ''
      } else {
        record.link = `https://workdrive.zohoexternal.com/external/${record.answer}`
      }
      record.imageUploaded = true
    }

    return record
  }

  result = parseLinksAndHasFlags(result)

  return result as ParsedQuestionnaire[]
}
